"use client";

// Lazy tool-output seam. A chat read that opted into `lazyToolOutputs`
// ships oversized tool outputs as placeholders (see
// `@magnus/contract/lazy-tool-output`); the host provides a resolver here
// and `ToolPart` hydrates a placeholder into the real output before handing
// the part to its registered view. The block never learns how the host
// fetches — tRPC, REST, anything that returns the output for a tool-call id.
import { createContext, type ReactNode, useContext, useRef } from "react";
export type FetchToolOutput = (toolCallId: string) => Promise<unknown>;
export type LazyToolOutputResult = {
  ok: true;
  output: unknown;
} | {
  ok: false;
  error: unknown;
};
export interface LazyToolOutputResolver {
  /**
   * Resolve one tool call's output. Returns the same promise for repeat
   * calls with the same id so React's `use()` can suspend on it across
   * renders; never rejects (failures resolve to `{ ok: false }` so a view
   * can degrade instead of tripping an error boundary). Failures stay
   * cached for the provider's lifetime — evicting them would turn every
   * re-render of the failed row into another request. Reopening the chat
   * (a fresh provider) retries.
   */
  resolve: (toolCallId: string) => Promise<LazyToolOutputResult>;
}
const LazyToolOutputContext = createContext<LazyToolOutputResolver | null>(null);

/**
 * Provides the resolver to every `ToolPart` below. The cache lives for the
 * provider's lifetime. Self-driving `MagnusChat` keys its session by chat id,
 * so it gets a fresh cache per chat; a controlled (`engine`) host that swaps
 * chats without remounting keeps one cache across them, which is safe
 * because tool-call ids are globally unique. The latest `fetchToolOutput` is
 * read through a ref, so hosts may pass an inline closure without busting
 * the cache every render.
 */
export function LazyToolOutputProvider({
  children,
  fetchToolOutput
}: {
  children: ReactNode;
  fetchToolOutput?: FetchToolOutput;
}) {
  const fetchRef = useRef(fetchToolOutput);
  fetchRef.current = fetchToolOutput;
  const resolverRef = useRef<LazyToolOutputResolver | null>(null);
  if (resolverRef.current === null) {
    const cache = new Map<string, Promise<LazyToolOutputResult>>();
    resolverRef.current = {
      resolve: toolCallId => {
        const cached = cache.get(toolCallId);
        if (cached) {
          return cached;
        }
        const fetchToolOutputNow = fetchRef.current;
        const promise: Promise<LazyToolOutputResult> = fetchToolOutputNow ? fetchToolOutputNow(toolCallId).then(output => ({
          ok: true,
          output
        }) as const, (error: unknown) => {
          // The row only says "Couldn't load"; keep the cause findable.
          // Failures are cached, so this logs once per tool call.
          console.warn(`[magnus-chat] Failed to load output for tool call ${toolCallId}:`, error);
          return {
            ok: false,
            error
          } as const;
        }) : Promise.resolve({
          ok: false,
          error: new Error("No tool-output resolver configured")
        } as const);
        cache.set(toolCallId, promise);
        return promise;
      }
    };
  }

  // Hosts that don't resolve lazy outputs simply don't provide a fetcher;
  // `ToolPart` then degrades to the generic row for placeholders.
  return <LazyToolOutputContext.Provider value={fetchToolOutput ? resolverRef.current : null}>
      {children}
    </LazyToolOutputContext.Provider>;
}

/** The host's lazy-output resolver, or `null` when none is configured. */
export function useLazyToolOutputResolver() {
  return useContext(LazyToolOutputContext);
}